// src/components/PendingBanner.js
// Shared alert stack shown at the top of the home screen: tool requests on my
// equipment (with a Decline button), my own outstanding requests, and incoming
// and outgoing transfers.
//
// Deliberately no automatic overdue/deadline banner. Tools are not given a due
// date, so there is nothing to be late for. If that is ever wanted it should be
// an admin-configurable option, not a fixed time rule applied to every tool.
//
// Extracted from Dashboard.js so the new HomePage and the legacy Dashboard
// render identical banners instead of drifting apart.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateToolRequest } from '../services/api';

export default function PendingBanner({
  toolRequests   = [],
  transfers      = [],
  onRequestDeclined,
}) {
  const [decliningId, setDecliningId] = useState(null);
  const navigate = useNavigate();

  const incoming            = transfers.filter(t => t.direction === 'incoming');
  const outgoing            = transfers.filter(t => t.direction === 'outgoing');
  const requestsOnMyTools   = toolRequests.filter(r => r.direction === 'incoming');
  const myOutgoingRequests  = toolRequests.filter(r => r.direction === 'outgoing');

  if (toolRequests.length === 0 && transfers.length === 0) return null;

  const handleDecline = async (requestId) => {
    setDecliningId(requestId);
    try {
      await updateToolRequest(requestId, { status: 'cancelled' });
      onRequestDeclined?.(requestId);
    } catch (err) {
      console.error('Failed to decline request:', err);
    } finally {
      setDecliningId(null);
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>

      {/* Someone wants a tool I hold */}
      {requestsOnMyTools.length > 0 && (
        <div style={{
          background: '#2d1f00', border: '1px solid #d97706',
          borderRadius: 10, padding: '12px 16px', marginBottom: 8,
        }}>
          <p style={{ color: '#fbbf24', fontWeight: 700, marginBottom: 6 }}>
            📋 {requestsOnMyTools.length} tool request{requestsOnMyTools.length > 1 ? 's' : ''} on your equipment
          </p>
          {requestsOnMyTools.map(r => (
            <div key={r.request_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, gap: 8 }}>
              <p style={{ color: '#fde68a', fontSize: '0.88rem', margin: 0, flex: 1 }}>
                {r.requested_by_name} needs: {r.item_name}{r.asset_tag ? ` (${r.asset_tag})` : ''}
                {r.requested_for_location_name ? ` — for ${r.requested_for_location_name}` : ''}
              </p>
              <button
                onClick={() => handleDecline(r.request_id)}
                disabled={decliningId === r.request_id}
                style={{
                  background: '#7f1d1d', color: '#fca5a5', border: '1px solid #dc2626',
                  borderRadius: 6, padding: '3px 10px', fontSize: '0.75rem',
                  fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                  opacity: decliningId === r.request_id ? 0.5 : 1
                }}
              >
                {decliningId === r.request_id ? '…' : 'Decline'}
              </button>
            </div>
          ))}
          <p style={{ color: '#d97706', fontSize: '0.78rem', marginTop: 6 }}>
            Coordinate with your foreman or return to warehouse.
          </p>
        </div>
      )}

      {/* My outgoing tool requests */}
      {myOutgoingRequests.length > 0 && (
        <div style={{
          background: '#1c1f2e', border: '1px solid #6366f1',
          borderRadius: 10, padding: '12px 16px', marginBottom: 8,
        }}>
          <p style={{ color: '#a5b4fc', fontWeight: 700, marginBottom: 6 }}>
            📋 {myOutgoingRequests.length} pending tool request{myOutgoingRequests.length > 1 ? 's' : ''} you submitted
          </p>
          {myOutgoingRequests.map(r => (
            <p key={r.request_id} style={{ color: '#c7d2fe', fontSize: '0.88rem', marginBottom: 2 }}>
              {r.item_name}{r.asset_tag ? ` (${r.asset_tag})` : ''}
              {r.current_holder_name ? ` — with ${r.current_holder_name}` : ''}
              {r.requested_for_location_name ? ` for ${r.requested_for_location_name}` : ''}
            </p>
          ))}
        </div>
      )}

      {/* Incoming transfers — the accept_url link is how a transfer is accepted */}
      {incoming.length > 0 && (
        <div style={{
          background: '#1a2e1a', border: '1px solid #22c55e',
          borderRadius: 10, padding: '12px 16px', marginBottom: 8,
        }}>
          <p style={{ color: '#22c55e', fontWeight: 700, marginBottom: 6 }}>
            📥 {incoming.length} incoming transfer{incoming.length > 1 ? 's' : ''} waiting for you
          </p>
          <button className="btn btn-success" style={{ minHeight: 40, marginBottom: 8, fontSize: '0.9rem' }}
            onClick={() => navigate('/incoming')}>
            Review &amp; accept{incoming.length > 1 ? ' (accept all or scan)' : ''}
          </button>
          {incoming.map(t => (
            <div key={t.transfer_id} style={{ marginBottom: 4 }}>
              <span style={{ color: '#ccc', fontSize: '0.9rem' }}>
                {t.item_name}{t.asset_tag ? ` (${t.asset_tag})` : ''} from {t.from_name}
              </span>
              {' '}
              <a href={t.accept_url}
                style={{ color: '#22c55e', fontSize: '0.85rem', textDecoration: 'underline' }}>
                Accept / Reject
              </a>
            </div>
          ))}
        </div>
      )}

      {/* Outgoing transfers */}
      {outgoing.length > 0 && (
        <div style={{
          background: '#1a1f2e', border: '1px solid #3b82f6',
          borderRadius: 10, padding: '12px 16px',
        }}>
          <p style={{ color: '#60a5fa', fontWeight: 700, marginBottom: 6 }}>
            📤 {outgoing.length} outgoing transfer{outgoing.length > 1 ? 's' : ''} awaiting acceptance
          </p>
          {outgoing.map(t => (
            <p key={t.transfer_id} style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: 2 }}>
              {t.item_name}{t.asset_tag ? ` (${t.asset_tag})` : ''} → {t.to_name}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
